import type { ManifestMap, MetadataMap } from '@xing-com/crate-core-assets';
import type { RouteMatcher } from '@xing-com/crate-core-router';
import type {
  ErrorReporter,
  LoadedXinglet,
  XingletModule,
} from '@xing-com/crate-xinglet';
import { EventEmitter, Eventual } from '@xing-com/crate-xinglet';
import type {
  BrowserRuntime,
  Runtime,
  RuntimeConfig,
  RuntimeEvents,
  ServerRequest,
  ServerResponse,
  ServerRuntime,
} from '@xing-com/crate-xinglet/internal';

import { createXinglet } from './create-xinglet';
import { extractMetadataMap } from './extract-metadata';

function withCache<T>(
  cache: Map<string, T>,
  callback: (key: string, ...rest: unknown[]) => T
): (key: string, ...rest: unknown[]) => T {
  return (key: string, ...rest) => {
    let value = cache.get(key);

    if (value === undefined) {
      value = callback(key, ...rest);
      cache.set(key, value);
    }

    return value;
  };
}

export interface CommonRuntimeOptions {
  cachedXinglets?: Map<string, Eventual<LoadedXinglet>>;
  config: RuntimeConfig;
  fetch?: typeof globalThis.fetch;
  languageData?: Record<string, string>;
  manifestMap: ManifestMap;
  matchRoute: RouteMatcher;
  metadataMap?: MetadataMap;
  reportError?: ErrorReporter;
  serverData: Record<string, unknown>;
}

export interface BrowserRuntimeOptions extends CommonRuntimeOptions {
  isServer: false;
  importModule<T>(name: string, entry?: string): Eventual<T>;
  importXinglet(name: string): Eventual<XingletModule>;
  registerMocks?(xingletName: string): Promise<void>;
}

export interface ServerRuntimeOptions extends CommonRuntimeOptions {
  isServer: true;
  importModule<T>(name: string, entry?: string): Eventual<T>;
  importXinglet(name: string): Eventual<XingletModule>;
  renderedXinglets: string[];
  request: ServerRequest;
  response: ServerResponse;
}

export type RuntimeOptions = BrowserRuntimeOptions | ServerRuntimeOptions;

export function createRuntime(options: BrowserRuntimeOptions): BrowserRuntime;
export function createRuntime(options: ServerRuntimeOptions): ServerRuntime;
export function createRuntime({
  cachedXinglets = new Map(),
  fetch = globalThis.fetch,
  importModule,
  importXinglet,
  languageData = {},
  manifestMap,
  matchRoute,
  metadataMap = extractMetadataMap(manifestMap),
  reportError = console.error,
  ...options
}: RuntimeOptions): Runtime {
  const runtime: Runtime = {
    ...options,

    cachedXinglets,
    events: new EventEmitter<RuntimeEvents>(),
    fetch,
    languageData,
    matchRoute,
    manifestMap,
    metadataMap,
    reportError,

    // oxlint-disable-next-line typescript/consistent-type-assertions
    host: undefined as never,

    loadModule<T>(name: string, entry?: string) {
      return importModule<T>(name, entry);
    },
    loadXinglet: withCache(cachedXinglets, (name) => {
      runtime.events.emit('xinglet-requested', name);

      return importXinglet(name).then((module) => {
        const xingletClass = module.default;
        const xinglet = xingletClass?.prototype?.constructor
          ? createXinglet(xingletClass, name, runtime)
          : undefined;

        return Eventual.resolve(
          module.Component ?? xinglet?.getComponent?.(runtime.host, runtime)
        ).then((Component) => {
          runtime.events.emit('xinglet-loaded', name);

          return {
            Component,
            module,
            xinglet,
          };
        });
      });
    }),
  };

  return runtime;
}
